import React, { useCallback, useState } from 'react'
import { Routes, Route, Link } from 'react-router-dom'

import styled, { ThemeProvider } from 'styled-components'
import Drawer from './components/Drawer'

import LoginForm from './components/auth/login-form'
import RegisterForm from './components/auth/register-form'
import CheckEmail from './components/auth/check-email'
import ConfirmRegistration from './components/auth/confirm-registration'
import ForgotPassword from './components/auth/forgot-password'
import NewPassword from './components/auth/new-password'

import Home from './components/Home'
import NotFound from './components/NotFound'
import Header from './styled/header'

import Settings from './components/settings/edit'

import SongList from './components/songs/list'
import SongEdit from './components/songs/edit'
import SongView from './components/songs/view'

import SetListList from './components/setlists/list'
import SetListEdit from './components/setlists/edit'
import SetListView from './components/setlists/view'
import SetListExport from './components/setlists/export'

import ShowCreate from './components/shows/create'
import ShowEdit from './components/shows/edit'
import ShowView from './components/shows/view'

import GroupList from './components/groups/list'
import GroupEdit from './components/groups/edit'
import GroupView from './components/groups/view'

import InvitationsList from './components/invitations/list'
import Faq from './components/faq'

import RehearseView from './components/rehearse/view'
import Rehearse from './components/rehearse'

import Pricing from './components/pricing'
import Marketing from './components/marketing'

import getTheme from './styled/theme'

import Loader from './styled/loader'

import { AuthProvider, AuthConsumer } from './context/auth'
import { SongListProvider } from './context/song-list'
import { RehearsalProvider, context as RehearsalContext } from './context/rehearsal'

import Recents from './components/menu/recents'
import RehearsalStatus from './components/rehearse/rehearsal-status'

import BodyComponent from './styled/body'

const MenuItemList = styled.div`
  color: #b8b7ad;
  padding: 0.8em;
  display: block;
  
  a {
    display: block;
    padding: 1em 0;
    font-size: 1.3em;
    border-bottom: 1px solid ${({ theme }) => theme.primary};
    transition: color 0.2s ease;
    
    &:hover {
      color: ${({ theme }) => theme.primary};
    }
    
    &:last-child {
      border-bottom: none;
    }
  }
`

const ConditionalHeader = ({ user, menuOpen, handleStateChange, closeMenu }) => {
  const { isFollowing } = React.useContext(RehearsalContext)

  // Hide header completely when following a rehearsal
  if (isFollowing) {
    return null
  }

  return (
    <Header>
      <h1><Link onClick={() => closeMenu()} to={(user) ? '/songs' : '/'} className='menu-item'>Tabify</Link></h1>
      {user && <RehearsalStatus />}
      <Drawer
        isOpen={menuOpen}
        onStateChange={(state) => handleStateChange(state)}
        right
      >
        <MenuItemList>
          {!user && <Link onClick={() => closeMenu()} to='/login'>login</Link>}
          {!user && <Link onClick={() => closeMenu()} to='/register'>sign up</Link>}
          {!user && <Link onClick={() => closeMenu()} to='/faq'>FAQ</Link>}
          {user && <Link onClick={() => closeMenu()} to='/songs'>songs</Link>}
          {user && <Link onClick={() => closeMenu()} to='/new-song'>&nbsp;&nbsp;&nbsp;+ song</Link>}
          {user && <Link onClick={() => closeMenu()} to='/setlists'>set lists</Link>}
          {user && <Link onClick={() => closeMenu()} to='/groups'>groups</Link>}
          {user && <Link onClick={() => closeMenu()} to='/invitations'>invitations</Link>}
          {user && <Link onClick={() => closeMenu()} to='/faq'>FAQ</Link>}
          {user && <Link onClick={() => closeMenu()} to='/settings'>settings</Link>}
          {user && <Link onClick={() => closeMenu()} to='/logout'>logout</Link>}
        </MenuItemList>
        <Recents />
      </Drawer>
    </Header>
  )
}

const App = () => {
  const [menuOpen, setMenuOpen] = useState(false)

  const handleStateChange = useCallback((state) => {
    setMenuOpen(state.isOpen)
  })

  const closeMenu = useCallback(() => {
    setMenuOpen(false)
  })

  return (
    <AuthProvider>
      <AuthConsumer>
        {({ logout, user, refreshUser }) => (
          <SongListProvider user={user}>
            <RehearsalProvider>
              <ThemeProvider theme={getTheme(user)}>
                <>
                  <BodyComponent className='App'>
                    <ConditionalHeader
                      user={user}
                      menuOpen={menuOpen}
                      handleStateChange={handleStateChange}
                      closeMenu={closeMenu}
                    />
                    {/*  ROUTES */}
                    <Routes>
                      <Route path='/' element={<Home />} />
                      <Route path='loader' element={<Loader />} />
                      <Route path='pricing' element={<Pricing />} />
                      <Route path='marketing' element={<Marketing />} />
                      <Route path='login' element={<LoginForm />} />
                      <Route path='register' element={<RegisterForm />} />
                      <Route path='confirm-email' element={<CheckEmail />} />
                      <Route path='confirm-registration/:token' element={<ConfirmRegistration />} />
                      <Route path='password-reset' element={<ForgotPassword />} />
                      <Route path='password-reset/confirm/:token' element={<NewPassword />} />
                      <Route path='faq' element={<Faq />} />
                      {user &&
                        <>
                          <Route path='settings' element={<Settings />} />
                          <Route path='songs' element={<SongList />} />
                          <Route path='new-song' element={<SongEdit />} />
                          <Route path='rehearse' element={<Rehearse />} />
                          <Route path='songs/:songid' element={<SongView />} />
                          <Route path='songs/:songid/edit' element={<SongEdit />} />

                          <Route path='setlists' element={<SetListList />} />
                          <Route path='new-setlist' element={<SetListEdit />} />
                          <Route path='setlists/:setlistid' element={<SetListView />} />
                          <Route path='setlists/:setlistid/edit' element={<SetListEdit />} />
                          <Route path='setlists/:setlistid/export' element={<SetListExport />} />

                          <Route path='setlists/:setlistid/song/:songid' element={<SongView />} />

                          <Route path='new-show' element={<ShowCreate />} />
                          <Route path='shows/:showid' element={<ShowView />} />
                          <Route path='shows/:showid/edit' element={<ShowEdit />} />

                          <Route path='groups' element={<GroupList />} />
                          <Route path='new-group' element={<GroupEdit />} />
                          <Route path='groups/:groupid' element={<GroupView />} />
                          <Route path='groups/:groupid/edit' element={<GroupEdit />} />

                          <Route path='invitations' element={<InvitationsList />} />

                          <Route path='rehearse/:rehearsalid' element={<RehearseView />} />
                        </>}
                      {!user &&
                        <>
                          <Route path='songs/:songid' element={<SongView />} />
                          <Route path='setlists/:setlistid' element={<SetListView />} />
                          <Route path='groups/:groupid' element={<GroupView />} />
                        </>}
                      <Route path='*' element={<NotFound />} />
                    </Routes>
                  </BodyComponent>
                </>
              </ThemeProvider>
            </RehearsalProvider>
          </SongListProvider>
        )}
      </AuthConsumer>

    </AuthProvider>
  )
}

export default App
